import { BuiltinDoorId } from "building-types/doors/BuiltinDoorId";
import type { GeneratedDoorPlacement, GeneratedDoorPlacementDeprecated } from "building-types/doors/doorTypes";
import { SwingSideGenerator } from "building-types/doors/doorTypes";
import type { DoorFuncOptions } from "building-types/openings/openingFuncOptions";
import { AwningType } from "building-types/settings/settingsTypesEnum";
import { assertIsNotUndefined } from "common/utils/assertOfType";
import getMergedDoorSettings from "doc/elements/doors/getMergedDoorSettings";
import { planarGraphAdjacentHalfEdge, planarGraphHalfEdgeLength } from "geom/planarGraph";
import { generatedDoorPlacementDeprecatedNew, doorDescriptorComputeHeight } from "../../doors/door";
import { isGarageDoorOperationType } from "../../doors/doorUtils";
import { computeOpeningZOffset } from "../openingPlacement";
import type { OpeningPlacementFuncOptions } from "../openingPlacementFuncOptions";
import { doorSwingsToOppositeWall, openingPlacementFuncOptionsInvert } from "../openingUtils";

export function doorPlacementFunc({
  doorType,
  center = false,
  offset = 4.5,
  swingSide = SwingSideGenerator.AGAINST_SOLID_WALL,
  awning,
  opts,
}: DoorFuncOptions & { opts: OpeningPlacementFuncOptions }): { doorPlacements: GeneratedDoorPlacement[] } | null {
  let descriptor = opts.doorCatalog[doorType];

  if (descriptor.id === BuiltinDoorId.NONE) {
    return null;
  }

  const doorBaseOffset = computeOpeningZOffset(opts.roomFloorAssemblies, opts.roomStart, opts.roomEnd);
  descriptor = doorDescriptorComputeHeight(descriptor, doorBaseOffset, opts.doorHomeSettings.door);

  const width = descriptor.width;

  const wallLength = planarGraphHalfEdgeLength(opts.planarGraph.planarGraph, opts.halfEdgeIndex);

  if (offset < 0) {
    offset = wallLength - width + offset;
  }

  if (width > wallLength - 6) {
    return null;
  }

  const oppHalfEdge = planarGraphAdjacentHalfEdge(opts.halfEdgeIndex);
  const streetOrientation = opts.planarGraphPerimeter.coveredPerimeterStreetOrientations[oppHalfEdge];

  // in cases where there is not enough space to swing the door, we reverse the door opening direction
  if (descriptor.requiresDoorSwing && doorSwingsToOppositeWall(opts, descriptor)) {
    if (!isGarageDoorOperationType({ descriptor })) {
      opts = openingPlacementFuncOptionsInvert(opts);
    }
  }

  const { roomStart, roomEnd, halfEdgeIndex, mergeSettingsForOpeningParams } = opts;
  const { activeSettingsSets, settings, entitySettings, optionChoices } = mergeSettingsForOpeningParams;

  const doorPlacements: (GeneratedDoorPlacement | GeneratedDoorPlacementDeprecated)[] = [];

  const { exteriorCasing, interiorCasing } = getMergedDoorSettings({
    homeSettings: opts.doorHomeSettings,
    activeSettingsSets,
    entitySettings,
    optionChoices,
    descriptor,
    settings,
  });

  if (center) {
    assertIsNotUndefined(roomStart);
    doorPlacements.push(
      generatedDoorPlacementDeprecatedNew({
        descriptor: descriptor,
        frontRoom: roomStart,
        backRoom: roomEnd,
        halfEdgeIndex,
        param: 0.5,
        paramNormalized: true,
        swingSide,
        awning,
        exteriorCasing,
        interiorCasing,
        awningSettings: { awningType: AwningType.DISABLED },
        streetOrientation,
      }),
    );
  } else {
    if (descriptor.width < 36) {
      assertIsNotUndefined(roomStart);
      // this is a hack to handle very tightly fit doors
      doorPlacements.push(
        generatedDoorPlacementDeprecatedNew({
          descriptor: descriptor,
          frontRoom: roomStart,
          backRoom: roomEnd,
          halfEdgeIndex,
          param: width / 2 + offset,
          paramNormalized: false,
          swingSide,
          awning,
          exteriorCasing,
          interiorCasing,
          awningSettings: { awningType: AwningType.DISABLED },
          streetOrientation,
        }),
      );
    } else {
      assertIsNotUndefined(roomStart);
      doorPlacements.push(
        generatedDoorPlacementDeprecatedNew({
          descriptor: descriptor,
          frontRoom: roomStart,
          backRoom: roomEnd,
          halfEdgeIndex,
          param: width / 2 + offset,
          paramNormalized: false,
          swingSide,
          awning,
          exteriorCasing,
          interiorCasing,
          awningSettings: { awningType: AwningType.DISABLED },
          streetOrientation,
        }),
      );
    }
  }

  return {
    doorPlacements,
  };
}
